import { Message, MessageType } from "./messageTypes";
import { BaseCommon } from "./namespace/Base";

export type DataFromType<Type extends MessageType> = Extract<
  Message,
  { type: Type }
>["data"];

export class ConnectionTimedOutError extends Error {}

export enum SyncStage {
  REGISTER_BASE,
  REGISTER_LOBBY,
  REGISTER_POKER,
  REGISTER_COOP_POKER,
}

export type WebSocketState =
  | typeof WebSocket.CONNECTING
  | typeof WebSocket.OPEN
  | typeof WebSocket.CLOSING
  | typeof WebSocket.CLOSED;

export type MessageHandler = (message: Message, clear: () => void) => void;
export type TypedListener<T extends MessageType> = (
  data: DataFromType<T>
) => void;
export type TypedPredicate<T extends MessageType> = (
  data: DataFromType<T>
) => boolean;

export class Connection {
  static HEARTBEAT_INTERVAL_MS = 5000;

  private ws: WebSocket;
  private handlers: Map<number, MessageHandler>;
  private disconnectHandlers: Map<number, () => void>;
  private handlerIndex: number;
  private heartbeatHandle: NodeJS.Timeout | undefined;
  private lastReceivedHeartbeat: number;
  private isLoaded: boolean;

  constructor(
    ws: WebSocket,
    onReadyStateChange: (state: WebSocketState) => void = () => {}
  ) {
    this.ws = ws;
    this.handlers = new Map();
    this.handlerIndex = 0;
    this.disconnectHandlers = new Map();
    this.lastReceivedHeartbeat = Date.now();

    this.handleMessageWithType(
      BaseCommon.TypeValue.DISCONNECT,
      ({ reason }) => {
        this.disconnect(`Peer closed connection with reason: ${reason}`);
      }
    );

    this.ws.onmessage = this.handleEvent.bind(this);
    this.ws.onopen =
      this.ws.onclose =
      this.ws.onerror =
        (() => {
          onReadyStateChange(this.ws.readyState as WebSocketState);
          if (
            this.isLoaded &&
            (this.ws.readyState === WebSocket.CLOSING ||
              this.ws.readyState === WebSocket.CLOSED)
          )
            this.disconnect("Peer closed unexpectedly", 0);
        }).bind(this);

    this.handleMessageWithType(
      BaseCommon.TypeValue.HEARTBEAT,
      (() => (this.lastReceivedHeartbeat = Date.now())).bind(this)
    );
    this.heartbeatHandle = setInterval(
      this.heartbeat.bind(this),
      Connection.HEARTBEAT_INTERVAL_MS
    );

    this.isLoaded = true;
  }

  handleMessage(handler: MessageHandler) {
    this.handlerIndex += 1;
    this.handlers.set(this.handlerIndex, handler);
    return this.handlerIndex;
  }

  handleMessageWithType<Type extends MessageType>(
    type: Type,
    handler: TypedListener<Type>
  ) {
    return this.handleMessage((message) => {
      if (message.type === type) {
        handler(message.data as DataFromType<Type>);
      }
    });
  }

  handleDisconnect(handler: () => void) {
    this.handlerIndex += 1;
    this.disconnectHandlers.set(this.handlerIndex, handler);
    return this.handlerIndex;
  }

  removeHandler(id: number) {
    return this.handlers.delete(id);
  }

  private handleEvent(event: MessageEvent) {
    const data = JSON.parse(event.data);
    const indicesToRemove: number[] = [];
    for (const [id, handler] of this.handlers.entries()) {
      try {
        handler(data as Message, () => {
          indicesToRemove.push(id);
        });
      } catch (e) {
        console.error(e);
      }
    }
    indicesToRemove.forEach((x) => this.handlers.delete(x));
  }

  awaitMessageWithType<Type extends MessageType>(
    type: Type,
    timeout: number,
    predicate: TypedPredicate<Type> = () => true
  ): Promise<DataFromType<Type>> {
    return new Promise((resolve, reject) => {
      const timeoutHandle =
        timeout == -1
          ? undefined
          : setTimeout(() => {
              reject(new ConnectionTimedOutError(`Timed out awaiting ${type}`));
            }, timeout);
      const handler: MessageHandler = (message, clear) => {
        if (message.type === type && predicate(message.data as any)) {
          clear();
          clearTimeout(timeoutHandle);
          resolve(message.data as any);
        }
      };
      this.handleMessage(handler);
    });
  }

  sendMessageWithType<Type extends MessageType>(
    type: Type,
    data: DataFromType<Type>
  ) {
    this.ws.send(JSON.stringify({ type: type, data: data }));
  }

  async sendAndAwait<
    SendType extends MessageType,
    ReceiveType extends MessageType
  >(
    sendType: SendType,
    data: DataFromType<SendType>,
    receiveType: ReceiveType,
    timeout: number,
    predicate: TypedPredicate<ReceiveType> = () => true
  ): Promise<DataFromType<ReceiveType>> {
    const deferred = this.awaitMessageWithType(receiveType, timeout, predicate);
    this.sendMessageWithType(sendType, data);
    return await deferred;
  }

  async sendRepeatedlyAndAwait<
    SendType extends MessageType,
    ReceiveType extends MessageType
  >(
    sendType: SendType,
    data: DataFromType<SendType>,
    receiveType: ReceiveType,
    timeout: number,
    sendInterval: number,
    predicate: TypedPredicate<ReceiveType> = () => true
  ): Promise<DataFromType<ReceiveType>> {
    const deferred = this.awaitMessageWithType(receiveType, timeout, predicate);
    const interval = setInterval(
      (() => this.sendMessageWithType(sendType, data)).bind(this),
      sendInterval
    );
    try {
      const result = await deferred;
      clearInterval(interval);
      return result;
    } catch (e) {
      clearInterval(interval);
      throw e;
    }
  }

  async clientSync(
    key: SyncStage,
    timeout: number,
    sendInterval: number = 1000
  ): Promise<void> {
    try {
      await this.sendRepeatedlyAndAwait(
        BaseCommon.TypeValue.SYNC,
        key,
        BaseCommon.TypeValue.SYNC,
        timeout,
        sendInterval,
        (k) => k === key
      );
    } catch (e) {
      if (e instanceof ConnectionTimedOutError) {
        throw new ConnectionTimedOutError(
          `Timed out syncing with key ${SyncStage[key]}`
        );
      }
      throw e;
    }
  }

  async serverSync(key: SyncStage, timeout: number) {
    try {
      await this.awaitMessageWithType(
        BaseCommon.TypeValue.SYNC,
        timeout,
        (k) => k === key
      );
      this.sendMessageWithType(BaseCommon.TypeValue.SYNC, key);
    } catch (e) {
      if (e instanceof ConnectionTimedOutError) {
        throw new ConnectionTimedOutError(
          `Timed out syncing with key ${SyncStage[key]}`
        );
      }
      throw e;
    }
  }

  getReadyState() {
    return this.ws.readyState;
  }

  disconnect(reason: string = "unknown", code: number = 0) {
    console.log(`Closing connection with reason: ${reason}`);
    this.isLoaded = false;
    this.sendMessageWithType(BaseCommon.TypeValue.DISCONNECT, {
      reason,
    });
    if (this.heartbeatHandle) clearInterval(this.heartbeatHandle);
    this.disconnectHandlers.forEach((x) => x());
    this.handlers.clear();
    this.ws.close(code, reason);
  }

  heartbeat() {
    this.sendMessageWithType(BaseCommon.TypeValue.HEARTBEAT, void 0);
    if (
      Date.now() - this.lastReceivedHeartbeat >
      3.5 * Connection.HEARTBEAT_INTERVAL_MS
    ) {
      this.disconnect("Lost hearbeat signal");
    }
  }
}
